import { ActionIcon, Card, Grid, Group, Select, Text, TextInput } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import ct from 'countries-and-timezones';
import { codes as countriesAndCodes } from 'country-calling-code';
import currencyCodes from 'currency-codes/data';
import ISO6391 from 'iso-639-1';
import { useEffect, useState } from 'react';
import TimePickerControl from '../../../components/TimePicker';
import platforms from '../../../constants/platforms';
import copyText from '../../../helpers/copyText';
import { DEFAULT_TIMEZONE, getCountryTimezones } from '../../../helpers/countryTimezones';
import { validateUrlProtocol } from '../../../helpers/validateURL';
import ErrorMessage from '../../ErrorMessage';
import RecommenderPageLimits from '../RecommenderPageLimits';
import SuperAdminArea from '../../SuperAdmin/SuperAdminArea';
import SuperAdminBadge from '../../SuperAdmin/SuperAdminBadge';
import TagsSelect from '../../TagSelector';
const SettingsTabGeneral = ({ domain, account, handleChange, isSuperAdmin }) => {
  const [userError, setUserError] = useState();

  const currencyOptions = currencyCodes.map((data) => ({ label: `${data.currency} (${data.code})`, value: data.code }));

  const countryOptions = countriesAndCodes.map((c) => ({
    label: `+${c.countryCodes}  ${c.country}`,
    value: c.country,
  }));

  const timezones = domain.country
    ? [...getCountryTimezones(domain.country), DEFAULT_TIMEZONE]
    : Object.keys(ct.getAllTimezones());
  const timezoneOptions = timezones.map((t) => ({ label: t, value: t }));

  const handleCountryChange = (value) => handleChange({ target: { name: 'country', value } });

  const handleTimezoneChange = (value) => handleChange({ target: { name: 'timezone', value } });

  const handleCurrencyChange = (value) => handleChange({ target: { name: 'currency', value } });

  const handleQuietHoursStartChange = (value) => handleChange({ target: { name: 'quietHoursStart', value } });

  const handleQuietHoursEndChange = (value) => handleChange({ target: { name: 'quietHoursEnd', value } });

  useEffect(() => {
    if (!domain.country && domain.timezone !== null) {
      handleChange({ target: { name: 'timezone', value: null } });
    }
  }, [domain.country, handleChange, domain.timezone]);

  // commenting out logo related elements, as we will not be generating them currently
  // not removing them because we will use them in another tool
  return (
    <Card withBorder>
      <Card.Section p="md">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Name</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="name"
              placeholder="My Awesome Shop"
              value={domain.name || ''}
              error={domain.name !== undefined && domain.name === '' ? 'name is required' : null}
              onChange={handleChange}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>URL</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              name="url"
              placeholder="https://myawesomeshop.com"
              value={domain.url}
              error={
                !validateUrlProtocol(domain.url)
                  ? 'The URL field is required. Please provide a URL with a valid protocol.'
                  : null
              }
              onChange={handleChange}
              disabled={account.partner}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Platform</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput
              disabled
              name="platform"
              value={platforms.find((p) => p.value === domain.platform)?.label || 'Unknown'}
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Country</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Select
              data={countryOptions}
              onChange={handleCountryChange}
              placeholder="Type to search..."
              value={domain.country}
              searchable
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Time Zone</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Select
              data={timezoneOptions}
              onChange={handleTimezoneChange}
              placeholder="Type to search..."
              value={domain.timezone}
              searchable
            />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Primary Language</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput disabled value={ISO6391.getName(domain.locale) || 'Unknown'} />
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <Group gap="xs">
              <label>Primary Currency</label>
              <SuperAdminBadge show={isSuperAdmin} tooltip="Only super admins can change the currency" />
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Select
              data={currencyOptions}
              disabled={!isSuperAdmin}
              value={domain.currency}
              onChange={handleCurrencyChange}
              placeholder="Type to search..."
              searchable
            />
            {isSuperAdmin && (
              <Text size="xs" c="dimmed" mt="xs">
                Important: Changing the currency will NOT automatically recalculate values recorded before the change!
              </Text>
            )}
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Shop Category</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput disabled value={domain.category || ''} />
          </Grid.Col>
        </Grid>

        <SuperAdminArea show={isSuperAdmin} mb="md">
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Tags</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <TagsSelect model={domain} handleChange={handleChange} setUserError={setUserError} />
              {userError && <ErrorMessage message={userError.message} />}
            </Grid.Col>
          </Grid>
          <Grid mb="md">
            <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
              <label>Branded Short Links</label>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 8 }}>
              <TextInput name="customDomainName" value={domain.customDomainName || ''} onChange={handleChange} />
              <Text size="xs" c="dimmed" mt="xs">
                You can customize short links with your own domain to enhance brand visibility and client trust,
                increasing the likelihood of link engagement
              </Text>
            </Grid.Col>
          </Grid>
        </SuperAdminArea>

        <RecommenderPageLimits domain={domain} handleChange={handleChange} isSuperAdmin={isSuperAdmin} />

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>Access Token</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Group gap="xs">
              <TextInput
                id="accessToken"
                readOnly
                name="accessToken"
                value={domain.accessToken || ''}
                style={{ flex: 1 }}
              />
              <ActionIcon variant="default" onClick={() => copyText('accessToken')}>
                <IconCopy size={16} />
              </ActionIcon>
            </Group>
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            Secret Key
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Group gap="xs">
              <TextInput id="secretKey" readOnly name="secretKey" value={domain.secretKey || ''} style={{ flex: 1 }} />
              <ActionIcon variant="default" onClick={() => copyText('secretKey')}>
                <IconCopy size={16} />
              </ActionIcon>
            </Group>
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }}>Quiet Hours</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TimePickerControl
                  prepend="between"
                  value={domain.quietHoursStart}
                  onChange={handleQuietHoursStartChange}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TimePickerControl
                  prepend="and"
                  append="local time"
                  value={domain.quietHoursEnd}
                  onChange={handleQuietHoursEndChange}
                />
              </Grid.Col>
            </Grid>
            <Text size="xs" c="dimmed" mt="xs">
              During these times, no marketing messages will be sent from campaigns and workflows.
            </Text>
          </Grid.Col>
        </Grid>
      </Card.Section>
    </Card>
  );
};

export default SettingsTabGeneral;
